// Lager-/Einkaufs-Domäne — reine, I/O-freie Logik & Konstanten für Warenbestand und Beschaffung.
//
// Mengen (Bestand, Sicherheitsbestand, Bestellmenge) werden als ganze ZEHNTEL geführt (1 Nachkommastelle) —
// exakt analog zu Geld-in-Cent (money.ts). Das vermeidet Float-Rundungsdrift bei Liter/kg-Beständen.
// 25 (Zehntel) = 2,5 Einheiten. Die DB-Spalten heißen *_tenths, die DTO-Felder *Tenths.
//
// Der Beschaffungs-Status (procurement_items.status) REUSED bewusst PART_STATES aus orders.ts
// (needed|ordered|delivered|installed) — kein zweiter Status-Satz, keine Begriffs-Drift.

/** Erlaubte Einheiten eines Lager-Teils (inventory_parts.unit). */
export const STOCK_UNITS = ['Stück', 'L', 'ml', 'kg', 'g', 'm'] as const
export type StockUnit = (typeof STOCK_UNITS)[number]
export function isStockUnit(value: unknown): value is StockUnit {
  return typeof value === 'string' && (STOCK_UNITS as readonly string[]).includes(value)
}

/** Erlaubte MwSt-Sätze in Prozent (inventory_parts.vat_rate). Ganze Zahlen (kein Float). */
export const VAT_RATES = [19, 7, 0] as const
export type VatRate = (typeof VAT_RATES)[number]
export function isVatRate(value: unknown): value is VatRate {
  return typeof value === 'number' && (VAT_RATES as readonly number[]).includes(value)
}

/**
 * Gründe einer Bestandsbewegung (stock_log.reason). Die RICHTUNG (Zu-/Abgang) ergibt sich aus der
 * aufrufenden Service-Funktion, nicht aus dem Vorzeichen der übergebenen Menge (die ist immer positiv).
 */
export const STOCK_REASONS = [
  'order_reserved', // Teil in einen Auftrag gebucht → Abgang
  'order_cancelled', // Auftrag/Position storniert → Rückbuchung (Zugang)
  'manual_adjustment', // manuelle absolute Korrektur (Inventar-Popup)
  'manual_withdrawal', // manuelle Entnahme → Abgang
  'manual_addition', // manuelle Zuführung → Zugang
  'restock_delivered', // Nachschub-Bestellung geliefert → Zugang
] as const
export type StockReason = (typeof STOCK_REASONS)[number]
export function isStockReason(value: unknown): value is StockReason {
  return typeof value === 'string' && (STOCK_REASONS as readonly string[]).includes(value)
}

/** Quelle eines Beschaffungs-Eintrags (procurement_items.source). */
export const PROCUREMENT_SOURCES = ['restock', 'order_oneoff'] as const
export type ProcurementSource = (typeof PROCUREMENT_SOURCES)[number]
export function isProcurementSource(value: unknown): value is ProcurementSource {
  return typeof value === 'string' && (PROCUREMENT_SOURCES as readonly string[]).includes(value)
}

/**
 * Parst eine Mengen-Eingabe robust nach ganzen Zehnteln. Akzeptiert deutsche ("2,5") und englische
 * ("2.5") Notation sowie Zahlen. Negativ/ungültig/leer → 0 (werkstatt-tauglich: keine negative Menge).
 * Beispiel: "2,5" → 25, "3" → 30, 1.25 → 13 (auf 1 NK gerundet).
 */
export function parseQtyTenths(input: string | number): number {
  const value = typeof input === 'number' ? input : parseFloat(String(input).trim().replace(',', '.'))
  return Number.isFinite(value) ? Math.max(0, Math.round(value * 10)) : 0
}

/** Formatiert Zehntel als deutsche Mengen-Anzeige: 25 → "2,5", 30 → "3" (ganze Zahl ohne Nachkomma). */
export function formatQty(tenths: number): string {
  const value = (tenths || 0) / 10
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',')
}

/** Marge in ganzen Cent (Verkauf − Einkauf), read-only-Anzeige im Teile-Popup. */
export function marginCents(sellingPriceCents: number, purchasePriceCents: number): number {
  return Math.round(sellingPriceCents || 0) - Math.round(purchasePriceCents || 0)
}

/**
 * Margenprozentsatz bezogen auf den Einkaufspreis (aufgerundet auf ganze Prozent).
 * null, wenn der EK 0 ist (Prozentsatz nicht sinnvoll berechenbar) — die UI zeigt dann "–".
 */
export function marginPercent(sellingPriceCents: number, purchasePriceCents: number): number | null {
  const pp = Math.round(purchasePriceCents || 0)
  if (pp <= 0) return null
  return Math.round(((Math.round(sellingPriceCents || 0) - pp) / pp) * 100)
}
